import React from 'react'

import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/heading'
import { EMAIL_ADDRESSES } from '@/lib/constants'
import { buildUrl } from '@/lib/helpers/helperFunctions'
import authService from '@/lib/services/authService'
import errorTypeService from '@/lib/services/errorTypeService'

interface Props {
  error?: unknown
}

const PublicEmailAliasRegistrationErrorMessage: React.FC<Props> = ({
  error,
}) => {
  // The backend embeds the addresses in brackets in the error message - the
  // only place the frontend can learn them, as session bootstrap failed. The
  // first is the blocked address, the second the alias owner's masked email.
  const bracketedEmails = errorTypeService.isApiError(error)
    ? [
        ...(error.response?.data['hydra:description']?.matchAll(
          /\(([^\s()]+@[^\s()]+)\)/g,
        ) ?? []),
      ].map((match) => match[1])
    : []
  const blockedEmail = bracketedEmails[0] ?? null
  const aliasOwnerEmail = bracketedEmails[1] ?? null

  const contactSupportUrl = buildUrl(`mailto:${EMAIL_ADDRESSES.rqSupport}`, {
    searchParams: {
      subject: 'Unable to sign up - email in use as a public-facing alias',
      body: `Hello,\n\nI tried to sign up to RQ with the email address ${blockedEmail ?? '[your email address]'} but was told it is in use as a public-facing alias on the platform.\n\n`,
    },
  })

  return (
    <>
      <Heading size={2}>This email address cannot be used</Heading>

      <p className="text-md mb-4">
        This email address {blockedEmail && <> ({blockedEmail})</>} is currently
        in use as an alias
        {aliasOwnerEmail && <> by another user ({aliasOwnerEmail})</>}. Please
        sign in with that account, or register with a different email address.
        <br />
        If you need any assistance, please contact support.
      </p>

      <div className="flex items-center gap-2">
        <Button variant="primary" onClick={() => authService.logout()}>
          Use a different account
        </Button>

        <Button variant="outline-secondary" asChild>
          <a href={contactSupportUrl}>Contact support</a>
        </Button>
      </div>
    </>
  )
}

export default PublicEmailAliasRegistrationErrorMessage
